import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import CustomerHeader from '../components/CustomerHeader';
import { supabase } from '../config/supabase';
import './CustomerProfile.css';

const CustomerProfile = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [isEditing, setIsEditing] = useState(false);
  const [customer, setCustomer] = useState(null);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [showDeleteSuccess, setShowDeleteSuccess] = useState(false);

  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    delivery_address: ''
  });

  useEffect(() => {
    checkAuth();
  }, []);

  const checkAuth = () => {
    try {
      const customerData = localStorage.getItem('customer');
      if (!customerData) {
        navigate('/login');
        return;
      }

      const customer = JSON.parse(customerData);
      setCustomer(customer);
      setFormData({
        name: customer.name || '',
        email: customer.email || '',
        phone: customer.phone || '',
        delivery_address: customer.delivery_address || ''
      });
      setLoading(false);
    } catch (error) {
      console.error('Error loading customer data:', error);
      navigate('/login');
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    setError('');
  };

  const handleEdit = () => {
    setIsEditing(true);
    setError('');
    setSuccess('');
  };

  const handleCancel = () => {
    // Reset form to original customer data
    setFormData({
      name: customer.name || '',
      email: customer.email || '',
      phone: customer.phone || '',
      delivery_address: customer.delivery_address || ''
    });
    setIsEditing(false);
    setError('');
    setSuccess('');
  };

  const handleSave = async (e) => {
    e.preventDefault();
    setSaving(true);
    setError('');
    setSuccess('');

    // Validation
    if (!formData.name.trim()) {
      setError('Name is required');
      setSaving(false);
      return;
    }

    if (!formData.email.trim()) {
      setError('Email is required');
      setSaving(false);
      return;
    }

    // Basic email validation
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(formData.email.trim())) {
      setError('Please enter a valid email address');
      setSaving(false);
      return;
    }

    try {
      if (!customer || !customer.id) {
        setError('Customer ID not found. Please login again.');
        setSaving(false);
        return;
      }

      // Update customer in Supabase
      const { data, error: updateError } = await supabase
        .from('customers')
        .update({
          name: formData.name.trim(),
          email: formData.email.trim().toLowerCase(),
          phone: formData.phone.trim() || null,
          delivery_address: formData.delivery_address.trim() || null
        })
        .eq('id', customer.id)
        .select()
        .single();

      if (updateError) {
        // Check if email already exists
        if (updateError.code === '23505') {
          setError('This email is already registered. Please use a different email.');
        } else {
          throw updateError;
        }
        setSaving(false);
        return;
      }

      // Update localStorage
      const updatedCustomer = {
        id: data.id,
        name: data.name,
        email: data.email,
        phone: data.phone,
        delivery_address: data.delivery_address
      };
      localStorage.setItem('customer', JSON.stringify(updatedCustomer));

      // Update state
      setCustomer(updatedCustomer);
      setIsEditing(false);
      setSuccess('Profile updated successfully!');

      // Clear success message after 3 seconds
      setTimeout(() => {
        setSuccess('');
      }, 3000);
    } catch (err) {
      console.error('Error updating profile:', err);
      setError(err.message || 'Failed to update profile. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteAccount = async () => {
    if (!customer || !customer.id) {
      setError('Customer ID not found. Please login again.');
      return;
    }

    setDeleting(true);
    setError('');

    try {
      // First, get the customer data to save to deleted_accounts
      const { data: customerData, error: fetchError } = await supabase
        .from('customers')
        .select('*')
        .eq('id', customer.id)
        .eq('is_active', true)
        .single();

      if (fetchError) {
        console.error('Error fetching customer:', fetchError);
        throw new Error('Failed to fetch account information.');
      }

      if (!customerData) {
        setError('Account not found or already deleted.');
        setDeleting(false);
        return;
      }

      // Insert into deleted_accounts table
      const { error: insertError } = await supabase
        .from('deleted_accounts')
        .insert([
          {
            customer_id: customerData.id,
            name: customerData.name,
            email: customerData.email,
            phone: customerData.phone,
            delivery_address: customerData.delivery_address,
            original_created_at: customerData.created_at,
            original_last_login: customerData.last_login,
            deletion_reason: 'User requested account deletion'
          }
        ]);

      if (insertError) {
        console.error('Error inserting into deleted_accounts:', insertError);
        // Continue even if this fails - we still want to deactivate the account
        console.warn('Could not save to deleted_accounts, but continuing with account deactivation');
      }

      // Deactivate the customer account (set is_active to false)
      const { error: updateError } = await supabase
        .from('customers')
        .update({ 
          is_active: false,
          updated_at: new Date().toISOString()
        })
        .eq('id', customer.id);

      if (updateError) {
        console.error('Error deactivating account:', updateError);
        throw new Error('Failed to deactivate account. Please try again.');
      }

      // Clear localStorage
      localStorage.removeItem('customer');
      localStorage.removeItem('restaurantCart');

      // Close confirmation modal
      setShowDeleteConfirm(false);
      
      // Show beautiful success popup
      setShowDeleteSuccess(true);
      
      // Redirect to login after 3 seconds (giving time to see the popup)
      setTimeout(() => {
        navigate('/login');
        window.location.reload();
      }, 3000);

    } catch (err) {
      console.error('Error deleting account:', err);
      setError(err.message || 'Failed to delete account. Please try again.');
      setDeleting(false);
      // Keep modal open so user can see the error
    }
  };

  if (loading) {
    return (
      <div className="customer-profile-page">
        <CustomerHeader />
        <div className="customer-profile-loading">
          <div className="loading-spinner">Loading...</div>
        </div>
      </div>
    );
  }

  return (
    <div className="customer-profile-page">
      <CustomerHeader />
      
      <div className="customer-profile-main">
        <div className="customer-profile-container">
          <div className="customer-profile-header">
            <h1 className="customer-profile-title">My Profile</h1>
            <p className="customer-profile-subtitle">Manage your account information</p>
          </div>

          {error && (
            <div className="customer-profile-message customer-profile-error">
              {error}
            </div>
          )}

          {success && (
            <div className="customer-profile-message customer-profile-success">
              {success}
            </div>
          )}

          <div className="customer-profile-card">
            <div className="customer-profile-card-header">
              <h2 className="customer-profile-card-title">Personal Information</h2>
              {!isEditing && (
                <button
                  className="customer-profile-edit-btn"
                  onClick={handleEdit}
                >
                  Edit
                </button>
              )}
            </div>

            <form onSubmit={handleSave} className="customer-profile-form">
              <div className="customer-profile-form-group">
                <label htmlFor="name" className="customer-profile-label">
                  Full Name <span className="customer-profile-required">*</span>
                </label>
                <input
                  type="text"
                  id="name"
                  name="name"
                  value={formData.name}
                  onChange={handleChange}
                  disabled={!isEditing}
                  className="customer-profile-input"
                  required
                />
              </div>

              <div className="customer-profile-form-group">
                <label htmlFor="email" className="customer-profile-label">
                  Email Address <span className="customer-profile-required">*</span>
                </label>
                <input
                  type="email"
                  id="email"
                  name="email"
                  value={formData.email}
                  onChange={handleChange}
                  disabled={!isEditing}
                  className="customer-profile-input"
                  required
                />
              </div>

              <div className="customer-profile-form-group">
                <label htmlFor="phone" className="customer-profile-label">
                  Phone Number
                </label>
                <input
                  type="tel"
                  id="phone"
                  name="phone"
                  value={formData.phone}
                  onChange={handleChange}
                  disabled={!isEditing}
                  className="customer-profile-input"
                  placeholder="+92 300 1234567"
                />
              </div>

              <div className="customer-profile-form-group">
                <label htmlFor="delivery_address" className="customer-profile-label">
                  Delivery Address
                </label>
                <textarea
                  id="delivery_address"
                  name="delivery_address"
                  value={formData.delivery_address}
                  onChange={handleChange}
                  disabled={!isEditing}
                  className="customer-profile-textarea"
                  rows="4"
                  placeholder="Enter your delivery address"
                />
              </div>

              {isEditing && (
                <div className="customer-profile-form-actions">
                  <button
                    type="button"
                    className="customer-profile-cancel-btn"
                    onClick={handleCancel}
                    disabled={saving}
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    className="customer-profile-save-btn"
                    disabled={saving}
                  >
                    {saving ? 'Saving...' : 'Save Changes'}
                  </button>
                </div>
              )}
            </form>
          </div>

          {/* Delete Account Section */}
          <div className="customer-profile-card customer-profile-delete-section">
            <div className="customer-profile-delete-header">
              <h2 className="customer-profile-card-title">Danger Zone</h2>
            </div>
            <div className="customer-profile-delete-content">
              <p className="customer-profile-delete-description">
                Once you delete your account, there is no going back. Please be certain.
              </p>
              <button
                type="button"
                className="customer-profile-delete-btn"
                onClick={() => {
                  setError('');
                  setShowDeleteConfirm(true);
                }}
                disabled={deleting}
              >
                Delete Account
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Delete Confirmation Modal */}
      {showDeleteConfirm && (
        <div className="customer-profile-modal-overlay" onClick={() => !deleting && setShowDeleteConfirm(false)}>
          <div className="customer-profile-modal" onClick={(e) => e.stopPropagation()}>
            <div className="customer-profile-modal-header">
              <h3 className="customer-profile-modal-title">Delete Account</h3>
              {!deleting && (
                <button
                  className="customer-profile-modal-close"
                  onClick={() => setShowDeleteConfirm(false)}
                >
                  ×
                </button>
              )}
            </div>
            <div className="customer-profile-modal-body">
              <p className="customer-profile-modal-text">
                Are you sure you want to delete your account? This action cannot be undone.
              </p>
              <p className="customer-profile-modal-warning">
                All your data, including order history, will be permanently deleted.
              </p>
              {error && (
                <div className="customer-profile-modal-error">
                  {error}
                </div>
              )}
            </div>
            <div className="customer-profile-modal-footer">
              <button
                type="button"
                className="customer-profile-modal-cancel-btn"
                onClick={() => setShowDeleteConfirm(false)}
                disabled={deleting}
              >
                Cancel
              </button>
              <button
                type="button"
                className="customer-profile-modal-delete-btn"
                onClick={handleDeleteAccount}
                disabled={deleting}
              >
                {deleting ? 'Deleting...' : 'Yes, Delete My Account'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Beautiful Success Popup */}
      {showDeleteSuccess && (
        <div className="customer-profile-success-overlay">
          <div className="customer-profile-success-popup">
            <div className="customer-profile-success-icon-container">
              <div className="customer-profile-success-checkmark">
                <svg className="customer-profile-success-svg" viewBox="0 0 52 52">
                  <circle className="customer-profile-success-circle" cx="26" cy="26" r="25" fill="none"/>
                  <path className="customer-profile-success-check" fill="none" d="M14.1 27.2l7.1 7.2 16.7-16.8"/>
                </svg>
              </div>
            </div>
            <h2 className="customer-profile-success-title">Account Deleted Successfully</h2>
            <p className="customer-profile-success-message">
              Your account has been permanently deleted. We're sorry to see you go!
            </p>
            <div className="customer-profile-success-footer">
              <p className="customer-profile-success-redirect">
                Redirecting to login page...
              </p>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default CustomerProfile;

